import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    agregarProcedimientos,
    resetProcedimientoFactura,
    updateProcedimientoFactura,
} from "../../../redux/actions";
import { ListadoPlanes } from "../../../Service/Service.Plan";
import Modal from "../../UI/Modal/Modal";
import { useToast } from "../../UI/Aviso";
import { montoLegible } from "../../../utils/facturaCrear";
import { tituloClinico } from "../../../utils/textoClinico";
import {
    aportaAlgo,
    efectoDelPlan,
    planesDeLaCobertura,
    totalDe,
    totalTrasAplicar,
} from "../../../utils/planFactura";
import "../../../styles/FacturaCrear.css";

/* ============================================================================
   CREAR FACTURA · FASE 3 — aplicar plan, con vista previa

   POR QUÉ ESTÁ SEPARADO DE "AGREGAR": aplicar un plan no trae líneas nuevas,
   toma las que ya están y les cambia el copago. Son dos verbos distintos y por
   eso son dos sitios distintos -- el botón ya vivía fuera del menú "+ Agregar"
   desde el rediseño anterior de la cabecera.

   LO QUE ARREGLA ESTE MODAL: antes el plan se aplicaba A CIEGAS. Cambiaba el
   total de la factura y no había forma de saber a cuántas líneas iba a afectar
   ni cuánto iba a quedar hasta después de pulsar. Ahora se ve antes.

   NADA DE LÓGICA CAMBIA. El cálculo es el de siempre
   (`calcularMontosCobertura`, ver utils/planFactura.js), se despacha la misma
   acción (`updateProcedimientoFactura`) con el mismo contenido, y el backend
   lo recalcula igual server-side al facturar. Lo único nuevo es que se enseña
   primero y que se puede deshacer.
   ========================================================================== */

function IconoCheck() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6L9 17l-5-5" />
        </svg>
    );
}

function IconoIgual() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M5 9h14" /><path d="M5 15h14" />
        </svg>
    );
}

export default function ModalAplicarPlan({
    abierto,
    onCerrar,
    pacienteSeleccionado,
    // Cuando se llega desde la barra de lote, el plan se aplica SÓLO a lo
    // seleccionado. Sin esto, a toda la factura.
    lineasObjetivo = null,
}) {
    const dispatch = useDispatch();
    const { mostrar } = useToast();
    // El `|| []` pegado al `useSelector` devuelve un array nuevo en cada render
    // cuando el estado viene vacío, y eso hace inútiles los `useMemo` de abajo.
    const enFacturaCrudo = useSelector((state) => state.facturaredux.procedimientos);
    const enFactura = useMemo(() => enFacturaCrudo || [], [enFacturaCrudo]);

    const [planes, setPlanes] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);
    const [elegido, setElegido] = useState("");
    /* LATINASALUD tiene 15 planes y el sistema 61: un `<select>` nativo obliga
       a recorrerlos con la rueda. Se escribe para filtrar, como en el resto de
       los selectores del sistema. */
    const [busqueda, setBusqueda] = useState("");

    const seguroId =
        pacienteSeleccionado?.seguros?.idseguro ?? pacienteSeleccionado?.seguro_id ?? null;

    /* Los planes NO se cachean, a propósito: crear un plan en otra pestaña del
       navegador y volver aquí tiene que enseñarlo. Es una lista de 61 filas
       (490 KB con todas las aseguradoras, mucho menos filtrada), así que
       pedirla en cada apertura no cuesta lo que costaría cachearla mal. */
    const pedirPlanes = useCallback(async () => {
        if (!seguroId) {
            setPlanes([]);
            return;
        }
        setCargando(true);
        setError(null);
        // CONFIG-PLANES-SEGURO-31: sólo planes Activos, igual que antes.
        const res = await ListadoPlanes({ estado: "Activo", seguro_id: seguroId });
        if (res?.data) setPlanes(res.data);
        else {
            setPlanes([]);
            setError(res?.message || "No se pudieron obtener los planes.");
        }
        setCargando(false);
    }, [seguroId]);

    useEffect(() => {
        if (!abierto) return;
        setElegido("");
        setBusqueda("");
        pedirPlanes();
    }, [abierto, pedirPlanes]);

    const disponibles = useMemo(() => planesDeLaCobertura(planes || [], seguroId), [planes, seguroId]);

    // Filtro por nombre, sin tildes ni mayusculas: el catalogo mezcla
    // "PLAN BASICO" con "Plan Básico" y quien escribe no tiene por que acertar.
    const normalizar = (v) =>
        String(v ?? "")
            .toLocaleLowerCase("es")
            .normalize("NFD")
            .replace(/[̀-ͯ]/g, "");

    const visibles = useMemo(() => {
        const t = normalizar(busqueda).trim();
        if (!t) return disponibles;
        return disponibles.filter((p) => normalizar(p.nombre).includes(t));
    }, [disponibles, busqueda]);

    const objetivo = useMemo(
        () => (Array.isArray(lineasObjetivo) && lineasObjetivo.length > 0 ? lineasObjetivo : enFactura),
        [lineasObjetivo, enFactura]
    );
    const esSubconjunto = Array.isArray(lineasObjetivo) && lineasObjetivo.length > 0;

    const plan = useMemo(
        () => disponibles.find((p) => String(p.id_plan) === String(elegido)) || null,
        [disponibles, elegido]
    );

    const efecto = useMemo(() => (plan ? efectoDelPlan(objetivo, plan) : null), [plan, objetivo]);
    const puedeAplicar = aportaAlgo(efecto);

    /* Aplicar. Con foto previa, porque `updateProcedimientoFactura` sobrescribe
       el precio original y sin la foto no habría a dónde volver. */
    const aplicar = () => {
        if (!efecto || !puedeAplicar) return;
        const antes = enFactura;
        dispatch(updateProcedimientoFactura(efecto.resultado));
        onCerrar();
        mostrar({
            tipo: "exito",
            texto: `Plan aplicado a ${efecto.cubiertos.length} procedimiento${
                efecto.cubiertos.length === 1 ? "" : "s"
            }`,
            accion: {
                label: "Deshacer",
                onClick: () => {
                    // Igual que en el quitado en lote: reset + agregar con la
                    // foto completa. Devuelve los precios Y el orden, con las
                    // acciones que ya existían.
                    dispatch(resetProcedimientoFactura());
                    dispatch(agregarProcedimientos(antes));
                },
            },
        });
    };

    const nombreSeguro = pacienteSeleccionado?.seguros?.nombre || null;

    return (
        <Modal
            abierto={abierto}
            titulo="Aplicar plan"
            ancho="formulario"
            onCerrar={onCerrar}
            nota={<span className="fcrear__nota">Podrás deshacerlo antes de emitir.</span>}
            secundario={{ label: "Cancelar" }}
            primario={{
                label: puedeAplicar
                    ? `Aplicar a ${efecto.cubiertos.length} procedimiento${
                          efecto.cubiertos.length === 1 ? "" : "s"
                      }`
                    : "Aplicar plan",
                disabled: !puedeAplicar,
                onClick: aplicar,
            }}
        >
            {/* Sobre QUÉ actúa. Es lo que faltaba: el modal viejo no decía si
                iba a tocar la factura entera o una parte. */}
            <p className="fplan__sobre" data-testid="plan-sobre">
                Modifica el copago de{" "}
                <b>
                    {esSubconjunto
                        ? `los ${objetivo.length} seleccionado${objetivo.length === 1 ? "" : "s"}`
                        : `los ${objetivo.length} procedimiento${
                              objetivo.length === 1 ? "" : "s"
                          } de la factura`}
                </b>
                . No agrega ninguna línea.
            </p>

            <div className="kc">
                <span className="kc__etiqueta" id="plan-etiqueta">Plan</span>

                {/* Buscador + lista, en vez de un `<select>`: con 15 planes por
                    aseguradora, recorrer el desplegable con la rueda es peor
                    que escribir tres letras. */}
                <span className="kc__caja fplan__buscador">
                    <input
                        type="text"
                        className="kc__control"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        onKeyDown={(e) => {
                            // Enter no envia: aqui no hay formulario y un submit
                            // accidental aplicaria el plan sin mirar la previa.
                            if (e.key === "Enter") e.preventDefault();
                        }}
                        placeholder={
                            cargando
                                ? "Cargando planes…"
                                : disponibles.length === 0
                                ? "No hay planes para esta cobertura"
                                : "Escribe para filtrar los planes"
                        }
                        disabled={cargando || disponibles.length === 0}
                        aria-label="Filtrar planes por nombre"
                        data-testid="plan-buscar"
                    />
                    {busqueda && (
                        <span className="fplan__contador">
                            {visibles.length} de {disponibles.length}
                        </span>
                    )}
                </span>

                {disponibles.length > 0 && (
                    <ul
                        className="fplan__lista"
                        role="listbox"
                        aria-labelledby="plan-etiqueta"
                        data-testid="plan-lista"
                    >
                        {visibles.length === 0 && (
                            <li className="fplan__sinresultados">Ningún plan coincide.</li>
                        )}
                        {visibles.map((p) => (
                            <li key={p.id_plan}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={String(p.id_plan) === String(elegido)}
                                    className={`fplan__opcion${
                                        String(p.id_plan) === String(elegido) ? " is-activa" : ""
                                    }`}
                                    onClick={() => setElegido(String(p.id_plan))}
                                    data-testid={`plan-opcion-${p.id_plan}`}
                                >
                                    <span className="fplan__opcion-nombre">
                                        {tituloClinico(p.nombre)}
                                    </span>
                                    <span className="fplan__opcion-meta">
                                        {(p.detalles || []).length} procedimiento
                                        {(p.detalles || []).length === 1 ? "" : "s"}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {nombreSeguro && (
                    <span className="kc__ayuda">
                        Sólo se listan los planes de {tituloClinico(nombreSeguro)}, la cobertura de
                        este paciente.
                    </span>
                )}
            </div>

            {error && (
                <p className="fplan__error" data-testid="plan-error">
                    {error}
                </p>
            )}

            {/* LA VISTA PREVIA. La parte importante: qué va a pasar, dicho en
                procedimientos y en dinero, antes de pulsar. */}
            {efecto && (
                <div className="fplan__previa" data-testid="plan-previa">
                    <div className="fplan__linea">
                        <span className="fplan__icono fplan__icono--ok">
                            <IconoCheck />
                        </span>
                        <span>
                            <b>{efecto.cubiertos.length}</b> procedimiento
                            {efecto.cubiertos.length === 1 ? "" : "s"} recibe
                            {efecto.cubiertos.length === 1 ? "" : "n"} copago del plan
                        </span>
                    </div>

                    {efecto.fuera.length > 0 && (
                        <div className="fplan__linea">
                            <span className="fplan__icono">
                                <IconoIgual />
                            </span>
                            <span>
                                <b>{efecto.fuera.length}</b> no{" "}
                                {efecto.fuera.length === 1 ? "está" : "están"} en el plan y
                                mantiene{efecto.fuera.length === 1 ? "" : "n"} su precio
                            </span>
                        </div>
                    )}

                    {/* El total con la MISMA fórmula que el Resumen de la
                        pantalla (subtotal + IVA): si aquí dijera otra cifra, la
                        vista previa sería peor que no tenerla. */}
                    <div className="fplan__total">
                        <span>Total de la factura</span>
                        <span className="fplan__total-cifras">
                            <span className="fplan__antes">${montoLegible(totalDe(enFactura))}</span>
                            <span aria-hidden="true">→</span>
                            <span className="fplan__despues" data-testid="plan-total-despues">
                                ${montoLegible(totalTrasAplicar(enFactura, efecto))}
                            </span>
                        </span>
                    </div>

                    {!puedeAplicar && (
                        <p className="fcrear__nota" data-testid="plan-sin-efecto">
                            Este plan no cubre ninguno de estos procedimientos, así que aplicarlo no
                            cambiaría nada.
                        </p>
                    )}
                </div>
            )}
        </Modal>
    );
}
